import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { STORAGE_KEYS } from '../../utils/constants';
import {
  loadGIS, initTokenClient, requestTokenAsync, msUntilTokenExpiry,
  revokeToken, hasValidToken, readFromDrive, writeToDrive, getUserEmail,
  listDriveFiles, createNamedFile, renameDriveFile,
} from '../../utils/googleDriveSync';
import { applyPayload } from '../../utils/migrate';

/**
 * Show a friendly toast for OAuth popup outcomes that are user actions or
 * environment issues rather than real failures. Returns true if it handled the
 * error (caller should stop), false to let the caller's own handling run.
 */
function handleOAuthError(err, showToast) {
  switch (err.message) {
    case 'popup_closed':
      showToast('Sign-in cancelled', 'info');
      return true;
    case 'popup_failed_to_open':
      showToast('Could not open Google sign-in — check that pop-ups are allowed for this site', 'error');
      return true;
    case 'TOKEN_TIMEOUT':
      showToast('Google sign-in timed out or was cancelled', 'error');
      return true;
    default:
      return false;
  }
}

/**
 * Manages Google Drive OAuth flow, token lifecycle, file selection, and
 * auto-save debounce. All token state lives in module-level vars inside
 * googleDriveSync.js — never in localStorage or React state.
 *
 * @param {{ savePayload: object, savePayloadRef: React.MutableRefObject, setters: object, showToast: Function }} params
 */
export function useDriveSync({ savePayload, savePayloadRef, setters, showToast }) {
  // builtInClientId is baked in at build time; always wins over localStorage
  // so a stale/invalid stored value can never block OAuth.
  const builtInClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
  const [_storedClientId, _setGoogleClientId] = useLocalStorage(STORAGE_KEYS.googleClientId, builtInClientId);
  const googleClientId = builtInClientId || _storedClientId;

  const [googleDriveEmail,    setGoogleDriveEmail]    = useLocalStorage(STORAGE_KEYS.googleDriveEmail,    '');
  const [googleDriveOn,       setGoogleDriveOn]       = useLocalStorage(STORAGE_KEYS.googleDriveOn,       false);
  const [googleDriveLastSync, setGoogleDriveLastSync] = useLocalStorage(STORAGE_KEYS.googleDriveSync,     null);
  const [googleDriveFileId,   setGoogleDriveFileId]   = useLocalStorage(STORAGE_KEYS.googleDriveFileId,   null);
  const [googleDriveFileName, setGoogleDriveFileName] = useLocalStorage(STORAGE_KEYS.googleDriveFileName, '');
  const [googleDriveAutoSave, setGoogleDriveAutoSave] = useLocalStorage(STORAGE_KEYS.googleDriveAutoSave, true);

  const [isDriveConnecting,  setIsDriveConnecting]  = useState(false);
  const [isDriveSyncing,     setIsDriveSyncing]      = useState(false);
  const [driveNeedsReconnect, setDriveNeedsReconnect] = useState(false);
  const [driveSyncError,     setDriveSyncError]       = useState(false);
  // True when local data has changed but hasn't been written to Drive yet.
  const [driveDirty,         setDriveDirty]          = useState(false);
  const [driveFiles,         setDriveFiles]          = useState([]);
  const [showFilePicker,     setShowFilePicker]      = useState(false);

  const skipNextDriveWriteRef    = useRef(false);
  const driveWriteTimerRef       = useRef(null);
  const proactiveRefreshTimerRef = useRef(null);
  const isDriveConnectingRef     = useRef(false);
  const googleDriveOnRef         = useRef(googleDriveOn);
  const autoSaveRef              = useRef(googleDriveAutoSave);
  useEffect(() => { googleDriveOnRef.current = googleDriveOn; }, [googleDriveOn]);
  useEffect(() => { autoSaveRef.current = googleDriveAutoSave; }, [googleDriveAutoSave]);
  useEffect(() => { isDriveConnectingRef.current = isDriveConnecting; }, [isDriveConnecting]);
  useEffect(() => () => clearTimeout(proactiveRefreshTimerRef.current), []);

  /** Validate + apply a data object loaded from Drive into all state setters. */
  const applyDriveData = useCallback((data) => {
    skipNextDriveWriteRef.current = true;
    try {
      applyPayload(data, setters);
    } catch (e) {
      skipNextDriveWriteRef.current = false;
      showToast(`Drive file is ${e.message} — keeping local data`, 'error');
    }
  }, [setters, showToast]);

  // Schedules a notification ~5 min before token expiry so the reconnect
  // modal appears before the next auto-save hits NO_TOKEN.
  function scheduleProactiveRefresh() {
    clearTimeout(proactiveRefreshTimerRef.current);
    const remaining = msUntilTokenExpiry();
    if (remaining <= 0) return;
    const delay = Math.max(1000, remaining - 300_000); // fire 5 min before expiry
    proactiveRefreshTimerRef.current = setTimeout(() => {
      if (!googleDriveOnRef.current) return;
      if (!hasValidToken()) setDriveNeedsReconnect(true);
    }, delay);
  }

  // On mount: if Drive was connected but we have no valid token, ask the user
  // to reconnect.  We never attempt a silent requestAccessToken here because
  // GIS always opens a popup window — even without a prompt — which browsers
  // block when there is no direct user gesture.
  useEffect(() => {
    if (!googleDriveOn || !googleClientId) return;
    if (!hasValidToken()) {
      setDriveNeedsReconnect(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Write the current local payload to Drive now. Clears the dirty flag on
  // success; used by both the auto-save debounce and the manual "Save now" button.
  const writeDriveNow = useCallback(async () => {
    if (!hasValidToken()) {
      if (!isDriveConnectingRef.current) setDriveNeedsReconnect(true);
      return false;
    }
    try {
      setIsDriveSyncing(true);
      await writeToDrive(savePayloadRef.current, googleDriveFileId);
      setGoogleDriveLastSync(new Date().toISOString());
      setDriveNeedsReconnect(false);
      setDriveSyncError(false);
      setDriveDirty(false);
      return true;
    } catch (err) {
      if (err.message === 'NO_TOKEN') {
        setDriveNeedsReconnect(true);
      } else {
        console.error('[Drive save]', err);
        setDriveSyncError(true);
        showToast('Drive sync failed: ' + err.message, 'error');
      }
      return false;
    } finally {
      setIsDriveSyncing(false);
    }
  // savePayloadRef is a ref; googleDriveFileId is the only changing value we read
  }, [googleDriveFileId, savePayloadRef, showToast, setGoogleDriveLastSync]);

  /** Manual push to Drive (for the Settings "Save now" button / auto-save off). */
  const saveToDriveNow = useCallback(async () => {
    if (!googleDriveOn || isDriveSyncing) return;
    const ok = await writeDriveNow();
    if (ok) showToast('Saved to Google Drive');
  }, [googleDriveOn, isDriveSyncing, writeDriveNow, showToast]);

  // On data change: mark dirty (local ahead of Drive), then auto-save after a
  // 3 s debounce — unless auto-save is turned off, in which case it stays dirty
  // until the user saves manually.
  useEffect(() => {
    if (!googleDriveOn) return;
    if (skipNextDriveWriteRef.current) { skipNextDriveWriteRef.current = false; return; }
    setDriveDirty(true);
    if (!autoSaveRef.current) return; // manual mode: stay dirty until user saves
    clearTimeout(driveWriteTimerRef.current);
    driveWriteTimerRef.current = setTimeout(() => { writeDriveNow(); }, 3000);
    return () => clearTimeout(driveWriteTimerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleDriveOn, savePayload]);

  /** Initiate Google OAuth flow and connect Drive sync. */
  const connectGoogleDrive = useCallback(async (clientId) => {
    const id = (clientId || googleClientId).trim();
    if (!id) return;
    setIsDriveConnecting(true);
    try {
      await loadGIS();
      initTokenClient(id, () => {});
      await requestTokenAsync(true); // show account picker

      const email = await getUserEmail();
      const who   = email ? `Connected as ${email}` : 'Connected to Google Drive';

      const files = await listDriveFiles();

      if (files.length === 0) {
        const newId = await createNamedFile('Personal Budget');
        await writeToDrive(savePayloadRef.current, newId);
        setGoogleDriveFileId(newId);
        setGoogleDriveFileName('Personal Budget');
        _setGoogleClientId(id);
        setGoogleDriveEmail(email);
        setGoogleDriveOn(true);
        setGoogleDriveLastSync(new Date().toISOString());
        setDriveNeedsReconnect(false);
        setDriveDirty(false);
        scheduleProactiveRefresh();
        showToast(`${who} — data saved to Drive`);
      } else if (files.length === 1) {
        const f = files[0];
        const driveData = await readFromDrive(f.id);
        if (driveData) applyDriveData(driveData);
        else await writeToDrive(savePayloadRef.current, f.id);
        setGoogleDriveFileId(f.id);
        setGoogleDriveFileName(f.name.replace(/\.json$/, ''));
        _setGoogleClientId(id);
        setGoogleDriveEmail(email);
        setGoogleDriveOn(true);
        setGoogleDriveLastSync(new Date().toISOString());
        setDriveNeedsReconnect(false);
        setDriveDirty(false);
        scheduleProactiveRefresh();
        showToast(`${who} — ${driveData ? 'data loaded from Drive' : 'data saved to Drive'}`);
      } else {
        // Multiple files — store credentials but wait for user to pick a file
        _setGoogleClientId(id);
        setGoogleDriveEmail(email);
        setDriveNeedsReconnect(false);
        setDriveFiles(files);
        setShowFilePicker(true);
        // googleDriveOn is set in selectDriveFile after the user picks
      }
    } catch (err) {
      if (handleOAuthError(err, showToast)) return;
      console.error('[Drive connect]', err);
      let msg = err.message;
      if (/not been used|disabled/i.test(msg))
        msg = 'Google Drive API is not enabled — go to Google Cloud Console → APIs & Services → Library and enable "Google Drive API"';
      else if (/insufficient.*scope/i.test(msg))
        msg = 'Missing OAuth scope — ensure drive.appdata and userinfo.email are on your consent screen';
      showToast('Drive error: ' + msg, 'error');
    } finally {
      setIsDriveConnecting(false);
    }
  }, [googleClientId, applyDriveData, showToast,
      _setGoogleClientId, setGoogleDriveEmail, setGoogleDriveOn, setGoogleDriveLastSync,
      setGoogleDriveFileId, setGoogleDriveFileName]);

  /**
   * Re-authenticate and resume sync on the already-known Drive file.
   * Used by the reconnect modal — avoids re-listing all files when we
   * already know which file to use.  Falls back to the full connect flow
   * if the file is no longer accessible.
   */
  const reconnectDrive = useCallback(async () => {
    if (!googleClientId) return;
    setIsDriveConnecting(true);
    isDriveConnectingRef.current = true;
    try {
      await loadGIS();
      initTokenClient(googleClientId, () => {});
      await requestTokenAsync(true);

      if (googleDriveFileId) {
        try {
          const data = await readFromDrive(googleDriveFileId);
          if (data) applyDriveData(data);
          else await writeToDrive(savePayloadRef.current, googleDriveFileId);
          setGoogleDriveLastSync(new Date().toISOString());
          setDriveNeedsReconnect(false);
          setDriveSyncError(false);
          setDriveDirty(false);
          scheduleProactiveRefresh();
          showToast('Reconnected to Google Drive');
          return;
        } catch {
          // File gone or inaccessible — fall through to full connect
        }
      }

      // No known file or it's gone: run the full first-connect flow
      await connectGoogleDrive(googleClientId);
    } catch (err) {
      if (handleOAuthError(err, showToast)) return;
      showToast('Drive reconnect failed: ' + err.message, 'error');
    } finally {
      setIsDriveConnecting(false);
      isDriveConnectingRef.current = false;
    }
  }, [googleClientId, googleDriveFileId, applyDriveData, connectGoogleDrive, showToast,
      setGoogleDriveLastSync]);

  /** Pull the latest data from Drive right now (manual sync). */
  const syncFromDrive = useCallback(async () => {
    if (!hasValidToken()) { setDriveNeedsReconnect(true); return; }
    try {
      setIsDriveSyncing(true);
      const data = await readFromDrive(googleDriveFileId);
      if (data) {
        applyDriveData(data);
        setGoogleDriveLastSync(new Date().toISOString());
        setDriveDirty(false);
        showToast('Synced from Google Drive');
      } else {
        showToast('No Drive data found', 'info');
      }
    } catch (err) {
      showToast('Sync error: ' + err.message, 'error');
    } finally {
      setIsDriveSyncing(false);
    }
  }, [googleDriveFileId, applyDriveData, showToast, setGoogleDriveLastSync]);

  /** Read Drive file and report what's in it without applying it locally. */
  const verifyDrive = useCallback(async () => {
    if (!hasValidToken()) { setDriveNeedsReconnect(true); return; }
    try {
      setIsDriveSyncing(true);
      const data = await readFromDrive(googleDriveFileId);
      if (!data) {
        showToast('No file found in Drive — data has not been written yet', 'info');
      } else {
        const txCount  = data.transactions?.length  ?? '?';
        const catCount = data.categories?.length    ?? '?';
        const bktCount = data.buckets?.length       ?? '?';
        showToast(`Drive file OK — ${txCount} transactions, ${catCount} categories, ${bktCount} buckets`, 'success');
      }
    } catch (err) {
      showToast('Verify error: ' + err.message, 'error');
    } finally {
      setIsDriveSyncing(false);
    }
  }, [googleDriveFileId, showToast]);

  const selectDriveFile = useCallback(async (file, newFileName) => {
    setShowFilePicker(false);
    try {
      let fileId, displayName;
      if (file) {
        fileId = file.id;
        displayName = file.name.replace(/\.json$/, '');
        const driveData = await readFromDrive(fileId);
        if (driveData) applyDriveData(driveData);
        else await writeToDrive(savePayloadRef.current, fileId);
      } else {
        const name = (newFileName || 'Personal Budget').trim();
        fileId = await createNamedFile(name);
        displayName = name;
        await writeToDrive(savePayloadRef.current, fileId);
      }
      setGoogleDriveFileId(fileId);
      setGoogleDriveFileName(displayName);
      setGoogleDriveOn(true);
      setGoogleDriveLastSync(new Date().toISOString());
      setDriveNeedsReconnect(false);
      setDriveDirty(false);
      scheduleProactiveRefresh();
      showToast(`Using "${displayName}"`);
    } catch (err) {
      showToast('Could not load Drive file: ' + err.message, 'error');
    }
  }, [applyDriveData, showToast,
      setGoogleDriveFileId, setGoogleDriveFileName, setGoogleDriveOn, setGoogleDriveLastSync]);

  /** Open the file picker with the current list of Drive files (mid-session switch). */
  const switchDriveFile = useCallback(async () => {
    if (!hasValidToken()) { setDriveNeedsReconnect(true); return; }
    try {
      setIsDriveSyncing(true);
      const files = await listDriveFiles();
      setDriveFiles(files);
      setShowFilePicker(true);
    } catch (err) {
      showToast('Could not list Drive files: ' + err.message, 'error');
    } finally {
      setIsDriveSyncing(false);
    }
  }, [showToast]);

  /** Rename the currently active Drive file. */
  const renameDriveFileActive = useCallback(async (newName) => {
    if (!googleDriveFileId) return;
    try {
      await renameDriveFile(googleDriveFileId, newName);
      setGoogleDriveFileName(newName);
      showToast(`Renamed to "${newName}"`);
    } catch (err) {
      showToast('Rename failed: ' + err.message, 'error');
    }
  }, [googleDriveFileId, showToast, setGoogleDriveFileName]);

  /** Disconnect Drive sync (keeps googleClientId so user can reconnect easily). */
  const disconnectGoogleDrive = useCallback(() => {
    revokeToken();
    clearTimeout(proactiveRefreshTimerRef.current);
    clearTimeout(driveWriteTimerRef.current);
    setGoogleDriveOn(false);
    setGoogleDriveEmail('');
    setGoogleDriveLastSync(null);
    setGoogleDriveFileId(null);
    setGoogleDriveFileName('');
    setDriveNeedsReconnect(false);
    setDriveSyncError(false);
    setDriveDirty(false);
    setShowFilePicker(false);
    showToast('Disconnected from Google Drive');
  }, [showToast, setGoogleDriveOn, setGoogleDriveEmail, setGoogleDriveLastSync,
      setGoogleDriveFileId, setGoogleDriveFileName]);

  // ─── Named helpers (replacing raw setter exposure) ────────────────────────────
  const updateGoogleClientId  = useCallback((id) => _setGoogleClientId(id), [_setGoogleClientId]);
  const openDriveFilePicker   = useCallback(() => setShowFilePicker(true),  []);
  const closeDriveFilePicker  = useCallback(() => setShowFilePicker(false), []);

  return useMemo(() => ({
    googleClientId, googleDriveEmail, googleDriveOn,
    googleDriveLastSync, googleDriveFileId, googleDriveFileName,
    isDriveConnecting, isDriveSyncing, driveNeedsReconnect, driveSyncError, driveDirty,
    googleDriveAutoSave, setGoogleDriveAutoSave, saveToDriveNow,
    driveFiles, showFilePicker,
    connectGoogleDrive, reconnectDrive, disconnectGoogleDrive, syncFromDrive, verifyDrive,
    selectDriveFile, switchDriveFile, renameDriveFileActive,
    updateGoogleClientId, openDriveFilePicker, closeDriveFilePicker,
  }), [
    googleClientId, googleDriveEmail, googleDriveOn,
    googleDriveLastSync, googleDriveFileId, googleDriveFileName,
    isDriveConnecting, isDriveSyncing, driveNeedsReconnect, driveSyncError, driveDirty,
    googleDriveAutoSave, setGoogleDriveAutoSave, saveToDriveNow,
    driveFiles, showFilePicker,
    connectGoogleDrive, reconnectDrive, disconnectGoogleDrive, syncFromDrive, verifyDrive,
    selectDriveFile, switchDriveFile, renameDriveFileActive,
    updateGoogleClientId, openDriveFilePicker, closeDriveFilePicker,
  ]);
}
